// frontend/src/context/PlatformAuthContext.js
//
// Session state for the superadmin console — the platform-side counterpart
// of context/AuthContext.js, kept entirely separate (own token key, own
// provider) so a platform session and a tenant session never interfere.
//
// The login flow has two shapes, matching worker/lib/platformAuth.js:
//   1. password  -> if the admin has MFA enrolled: { mfaRequired, token }
//                   (token is a short-lived "pending" token, NOT a session)
//                -> else: a full session token straight away (but the
//                   backend still blocks every mutating action until the
//                   admin enrols MFA + elevates).
//   2. mfa(pendingToken, code) -> full session token.
//
// "Elevation" (a 15-min write capability obtained with a reason string) is
// also handled here: elevate() swaps the stored token for the elevated one
// and we surface `elevated` / `elevateExp` for the UI countdown. It expires
// on its own server-side; refreshMe() re-reads the live state.
import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  platformFetch,
  platformJson,
  getPlatformToken,
  setPlatformToken,
  clearPlatformToken,
} from '../api/platformClient';

export const PlatformAuthContext = createContext();

export function PlatformAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, if a token is already in storage, validate it via /me.
  const refreshMe = useCallback(async () => {
    if (!getPlatformToken()) {
      setAdmin(null);
      return null;
    }
    try {
      const data = await platformJson('platform/me');
      setAdmin(data.admin);
      return data.admin;
    } catch {
      // Bad / expired token — drop it.
      clearPlatformToken();
      setAdmin(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe]);

  // Step 1. Returns { mfaRequired: true, pendingToken } when a second step
  // is needed, or { mfaRequired: false } once fully signed in.
  const login = async (email, password) => {
    const res = await platformFetch('platform/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Invalid credentials');
    }
    if (data.mfaRequired) {
      return { mfaRequired: true, pendingToken: data.token };
    }
    setPlatformToken(data.token);
    setAdmin(data.admin);
    return { mfaRequired: false };
  };

  // Step 2. Exchanges the pending token + a TOTP code for a session token.
  const submitMfa = async (pendingToken, code) => {
    const res = await platformFetch('platform/auth/mfa', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: pendingToken, code }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Incorrect code');
    }
    setPlatformToken(data.token);
    setAdmin(data.admin);
    return true;
  };

  // Begin TOTP enrolment — returns { otpauthUri, secret } to show a QR /
  // manual key. Does not complete until verifyMfaEnrollment().
  const beginMfaEnrollment = async () => {
    return platformJson('platform/auth/mfa/enroll', { method: 'POST' });
  };
  const verifyMfaEnrollment = async (code) => {
    await platformJson('platform/auth/mfa/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    await refreshMe();
    return true;
  };

  // Obtain a 15-min elevated (write) capability. Swaps the stored token.
  const elevate = async (reason) => {
    const res = await platformFetch('platform/elevate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Could not elevate');
    }
    setPlatformToken(data.token);
    await refreshMe();
    return data.elevateExpiresIn;
  };

  const logout = () => {
    clearPlatformToken();
    setAdmin(null);
  };

  return (
    <PlatformAuthContext.Provider
      value={{
        admin,
        loading,
        login,
        submitMfa,
        beginMfaEnrollment,
        verifyMfaEnrollment,
        elevate,
        refreshMe,
        logout,
      }}
    >
      {children}
    </PlatformAuthContext.Provider>
  );
}
